/**
 * HeroSection.js — Hero della home del sito pubblico di un noleggio.
 *
 * Supporta più temi decorativi selezionabili dall'admin in SitoSettings.
 * Il tema viene salvato in settings.sito.heroTema e passato come prop.
 * Temi disponibili: cerchi (default), onde, griglia, triangoli, punti, nessuno.
 */

import React from 'react';

/**
 * getReadableTextColor — sceglie testo bianco o scuro in base alla
 * luminanza dello sfondo, così un colore secondario chiaro (es. giallo,
 * bianco) non rende il testo del pulsante illeggibile.
 */
const getReadableTextColor = (hex) => {
  if (!hex) return '#ffffff';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#ffffff';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  // Luminanza percepita (formula standard YIQ)
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance > 165 ? '#1e293b' : '#ffffff';
};

// ── Decorazioni SVG per ogni tema ────────────────────────────────────────────

const DecoCircles = ({ accent }) => (
  <>
    <div className="pointer-events-none absolute -right-16 -top-16 w-72 h-72 rounded-full bg-white/5"></div>
    <div className="pointer-events-none absolute -left-10 -bottom-16 w-64 h-64 rounded-full bg-white/5"></div>
    <div className="pointer-events-none absolute right-1/4 -bottom-24 w-96 h-96 rounded-full bg-white/[0.03]"></div>
  </>
);

const DecoWaves = () => (
  <svg className="pointer-events-none absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 340" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 200 Q300 120 600 200 Q900 280 1200 200 L1200 340 L0 340 Z" fill="rgba(255,255,255,0.05)" />
    <path d="M0 240 Q300 160 600 240 Q900 320 1200 240 L1200 340 L0 340 Z" fill="rgba(255,255,255,0.04)" />
    <path d="M0 100 Q200 60 400 100 Q600 140 800 80 Q1000 20 1200 80 L1200 0 L0 0 Z" fill="rgba(255,255,255,0.04)" />
  </svg>
);

const DecoGrid = () => (
  <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.07]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 400 340" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.8"/>
      </pattern>
    </defs>
    <rect width="400" height="340" fill="url(#grid)" />
  </svg>
);

const DecoTriangles = () => (
  <svg className="pointer-events-none absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 340" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon points="0,340 240,0 480,340" fill="rgba(255,255,255,0.04)" />
    <polygon points="200,340 520,0 840,340" fill="rgba(255,255,255,0.03)" />
    <polygon points="700,340 960,40 1200,340" fill="rgba(255,255,255,0.04)" />
    <polygon points="900,0 1100,340 1200,200" fill="rgba(255,255,255,0.03)" />
    <polygon points="-100,100 100,340 300,100" fill="rgba(255,255,255,0.03)" />
  </svg>
);

const DecoDots = () => (
  <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.12]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 400 340" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="dots" width="28" height="28" patternUnits="userSpaceOnUse">
        <circle cx="4" cy="4" r="1.5" fill="white" />
      </pattern>
    </defs>
    <rect width="400" height="340" fill="url(#dots)" />
  </svg>
);

const DecoHexagons = () => (
  <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.06]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 400 340" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="hex" width="56" height="48" patternUnits="userSpaceOnUse">
        <polygon points="28,2 52,14 52,34 28,46 4,34 4,14" fill="none" stroke="white" strokeWidth="1.2"/>
      </pattern>
    </defs>
    <rect width="400" height="340" fill="url(#hex)" />
  </svg>
);

const DecoDiamonds = () => (
  <svg className="pointer-events-none absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 340" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="80" y="40" width="120" height="120" rx="4" transform="rotate(45 80 40)" fill="rgba(255,255,255,0.05)" />
    <rect x="350" y="180" width="80" height="80" rx="3" transform="rotate(45 350 180)" fill="rgba(255,255,255,0.04)" />
    <rect x="750" y="20" width="160" height="160" rx="4" transform="rotate(45 750 20)" fill="rgba(255,255,255,0.04)" />
    <rect x="1050" y="150" width="100" height="100" rx="3" transform="rotate(45 1050 150)" fill="rgba(255,255,255,0.05)" />
    <rect x="550" y="240" width="60" height="60" rx="2" transform="rotate(45 550 240)" fill="rgba(255,255,255,0.04)" />
  </svg>
);

const TEMI = {
  cerchi:    DecoCircles,
  onde:      DecoWaves,
  griglia:   DecoGrid,
  triangoli: DecoTriangles,
  punti:     DecoDots,
  esagoni:   DecoHexagons,
  diamanti:  DecoDiamonds,
  nessuno:   () => null,
};

const HeroSection = ({ displayName, slogan, bannerImage, accentColor = '#2563EB', secondaryColor, heroTema = 'cerchi', onCtaClick }) => {
  const Deco = TEMI[heroTema] || TEMI.cerchi;
  // Sfondo del pulsante: colore secondario se impostato, altrimenti il
  // primario (comportamento invariato per chi non l'ha personalizzato).
  const ctaColor = secondaryColor || accentColor;
  const ctaTextColor = getReadableTextColor(ctaColor);

  return (
    <div className="relative w-full overflow-hidden" style={{ minHeight: '340px' }}>
      {/* Sfondo: foto di copertina se presente, altrimenti gradiente sul brand */}
      <div className="absolute inset-0">
        {bannerImage ? (
          <img src={bannerImage} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full"
            style={{
              background: `linear-gradient(135deg, ${accentColor} 0%, color-mix(in srgb, ${accentColor} 55%, black) 100%)`,
            }}
          />
        )}
        {/* Overlay scuro per leggibilità del testo, più marcato se c'è una foto */}
        <div
          className="absolute inset-0"
          style={{
            background: bannerImage
              ? 'linear-gradient(180deg, rgba(15,23,42,0.35) 0%, rgba(15,23,42,0.75) 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 100%)',
          }}
        />
      </div>

      {/* Decorazione tema selezionato */}
      <Deco accent={accentColor} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col items-center justify-center text-center py-16 sm:py-20" style={{ minHeight: '340px' }}>
        <h1 className="text-3xl sm:text-5xl font-display font-extrabold text-white tracking-tight leading-tight max-w-2xl">
          {displayName}
        </h1>
        {slogan && (
          <p className="text-white/80 text-base sm:text-lg mt-3 max-w-xl">{slogan}</p>
        )}
        <button
          onClick={onCtaClick}
          className="mt-8 px-7 py-3.5 rounded-full font-bold text-sm tracking-wide hover:scale-105 active:scale-95 transition shadow-lg shadow-black/10"
          style={{ background: ctaColor, color: ctaTextColor }}
        >
          Scegli il tuo veicolo ↓
        </button>
      </div>
    </div>
  );
};

export { TEMI, getReadableTextColor };
export default HeroSection;
